import { compactNumber } from "@/lib/formatNumber";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { requireLogin } from "@/lib/guestGate";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogBody } from "@/components/ui/dialog";
import { Church, MapPin, ShieldCheck, Plus, Search, User, Bell, Check, Users, Sparkles, LocateFixed, Radio, CalendarDays, Clock, Loader2, Globe, X } from "lucide-react";
import { toast } from "sonner";
import { ChurchForm, emptyChurch, churchFormValid, SUGGEST_REQUIRED_KEYS } from "@/components/ChurchForm";
import { resolveMediaUrl } from "@/components/FileUpload";
import { imageVariant } from "@/lib/mediaUrl";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { ChurchCoverFallback } from "@/components/ChurchCoverFallback";
import { countryLabel } from "@/lib/countries";
import { denominationLabel } from "@/lib/denominations";
import { CardGridSkeleton } from "@/components/FeedSkeleton";
import { getCurrentPosition, isLocationDenied } from "@/lib/geo";
import { NearbyMiniMap, useNearbyMapPoints } from "@/components/NearbyMiniMap";

// Play cover video on hover (pointer devices); show a static cover image on touch/mobile.
const useHoverCapable = () => {
  const [h, setH] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    setH(mq.matches);
    const on = (e) => setH(e.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return h;
};

const ChurchCover = ({ church, coverVideoOn, t }) => {
  const hoverable = useHoverCapable();
  const vidRef = React.useRef(null);
  const hasVideo = coverVideoOn && !!church.cover_video_url;
  const cover = church.cover_url ? imageVariant(church.cover_url, 640) : null;
  const play = () => { try { vidRef.current?.play(); } catch { /* noop */ } };
  const stop = () => { try { const v = vidRef.current; if (v) { v.pause(); v.currentTime = 0; } } catch { /* noop */ } };
  return (
    <div
      className="relative -mx-5 -mt-5 mb-4 h-28 overflow-hidden rounded-t-2xl"
      onMouseEnter={hoverable && hasVideo ? play : undefined}
      onMouseLeave={hoverable && hasVideo ? stop : undefined}
      data-testid={`church-card-cover-${church.id}`}
    >
      {hasVideo && hoverable ? (
        <video
          ref={vidRef}
          src={resolveMediaUrl(church.cover_video_url)}
          poster={cover || NEUTRAL_POSTER}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={`church-card-video-${church.id}`}
        />
      ) : cover ? (
        <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <ChurchCoverFallback church={church} className="absolute inset-0" />
      )}
      {hasVideo && (
        <span className="absolute bottom-1.5 right-2 text-[10px] font-bold text-white px-1.5 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.45)" }} data-testid={`church-card-video-badge-${church.id}`}>
          ▶ {t("churches.preview", "Aperçu")}
        </span>
      )}
    </div>
  );
};


export const Churches = () => {
  const { t, user, config, lang } = useApp();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const coverVideoOn = config?.settings?.church?.cover_video !== false;
  const [churches, setChurches] = useState([]);
  useSeo({ title: t("nav.churches", "Églises") + " sur Yalola — directs, annonces et rediffusions", description: t("seo.churches_desc", "Annuaire des églises présentes sur Yalola : cultes en direct, annonces, événements et rediffusions de la communauté chrétienne."), canonical: "/churches", lang }, [lang]);
  const [spotlight, setSpotlight] = useState([]);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [denomination, setDenomination] = useState("");
  const [officialOnly, setOfficialOnly] = useState(false);
  const [myLangOnly, setMyLangOnly] = useState(true);
  const myLang = (user?.language || lang || "fr").toLowerCase().slice(0, 2);
  const mlDiscoveryOn = (config?.settings?.features?.multilingual_discovery) !== false;
  const showLangUi = (config?.settings?.features?.language_filter_ui) !== false;
  const [locating, setLocating] = useState(false);
  const [geo, setGeo] = useState(null); // {lat, lon, radiusKm} when "Autour de moi" uses GPS
  const [facets, setFacets] = useState({ countries: [], denominations: [], cities: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [suggest, setSuggest] = useState(emptyChurch());
  const canSuggest = ["preacher", "super_admin", "manager", "moderator"].includes(user?.role);
  const suggestHandledRef = React.useRef(false);
  const prefillResponsible = React.useCallback(() => {
    if (user?.role === "preacher") {
      setSuggest((s) => (s.pastor_user_id ? s : { ...s, pastor_user_id: user.id, pastor_name: user.name, pastor_verified: !!user.is_verified }));
    }
  }, [user]);

  const load = async (ov = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const s = ov.search ?? search, de = ov.denomination ?? denomination, off = ov.officialOnly ?? officialOnly;
      const ml = (ov.myLangOnly ?? myLangOnly) && mlDiscoveryOn;
      if (ml && myLang) params.set("lang", myLang);
      const hasGeo = ov.lat != null && ov.lon != null;
      if (s) params.set("search", s);
      if (de) params.set("denomination", de);
      if (off) params.set("official_only", "true");
      if (hasGeo) {
        params.set("lat", ov.lat);
        params.set("lon", ov.lon);
        params.set("radius_km", ov.radiusKm ?? (config?.settings?.church_radius_km || 25));
      } else {
        const co = ov.country ?? country, ci = ov.city ?? city;
        if (co) params.set("country", co);
        if (ci) params.set("city", ci);
      }
      const { data } = await api.get(`/churches?${params.toString()}`);
      setChurches(data.churches || []);
    } catch { setChurches([]); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    api.get("/churches/facets").then(({ data }) => setFacets(data || { countries: [], denominations: [], cities: [] })).catch(() => {});
    api.get("/churches/spotlight").then(({ data }) => setSpotlight(data.churches || [])).catch(() => setSpotlight([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noFilters = !search && !country && !city && !denomination && !officialOnly && !geo;

  const nearMe = async () => {
    // "Autour de moi" MUST use the device's real GPS position (native plugin on the
    // app, browser geolocation on web). We never fall back to the profile city — a
    // user in China with a Brussels profile must be searched from where they ARE.
    setLocating(true);
    // Switching to a proximity search is a mode switch → clear the "Officielles" toggle
    // right away so it never stays stuck active (regardless of the GPS outcome).
    setOfficialOnly(false);
    try {
      const { lat, lon } = await getCurrentPosition({ timeout: 9000 });
      const radiusKm = config?.settings?.church_radius_km || 25;
      // A proximity search should return every nearby church regardless of filters —
      // clear country/city/denomination/language so nothing is silently applied.
      setSearch(""); setDenomination(""); setCountry(""); setCity(""); setMyLangOnly(false);
      setGeo({ lat, lon, radiusKm });
      load({ search: "", denomination: "", country: "", city: "", officialOnly: false, myLangOnly: false, lat, lon, radiusKm });
      toast.success(t("churches.radius_toast", "Églises dans un rayon de {km} km autour de toi 📍").replace("{km}", radiusKm));
    } catch (e) {
      // GPS refused/unavailable: no fake "profile city" fallback. Reflect the cleared
      // "Officielles" toggle in the list so nothing stays silently applied.
      setGeo(null);
      load({ officialOnly: false, lat: null, lon: null });
      toast.info(isLocationDenied(e)
        ? "Autorise la localisation pour trouver les églises autour de toi 📍"
        : "Localisation indisponible pour le moment. Réessaie, ou recherche par ville/pays.");
    } finally {
      setLocating(false);
    }
  };
  const resetFilters = () => { setGeo(null); setSearch(""); setCountry(""); setCity(""); setDenomination(""); setOfficialOnly(false); load({ search: "", country: "", city: "", denomination: "", officialOnly: false }); };
  // Refine the GPS "Autour de moi" search radius (5 / 10 / 25 / 50 km) on the fly.
  const setRadius = (km) => {
    if (!geo) return;
    setGeo({ ...geo, radiusKm: km });
    load({ lat: geo.lat, lon: geo.lon, radiusKm: km });
  };
  // Combined nearby points (directs + églises) for the proximity mini-map.
  const churchMapPoints = useNearbyMapPoints(config?.settings?.nearby_map_enabled && geo ? geo : null);
  const suggestOptions = React.useMemo(() => Array.from(new Set([...(churches || []).map((c) => c.name), ...facets.cities])).filter(Boolean), [churches, facets.cities]);

  // Open the "Suggérer une église" dialog when reached from the mobile Create menu (?suggest=1).
  useEffect(() => {
    if (searchParams.get("suggest") !== "1" || suggestHandledRef.current) return;
    if (user === undefined) return; // wait for auth to resolve
    suggestHandledRef.current = true;
    searchParams.delete("suggest");
    setSearchParams(searchParams, { replace: true });
    if (canSuggest) { prefillResponsible(); setDialogOpen(true); }
    else toast.info(t("churches.preachers_only", "Seuls les prédicateurs peuvent suggérer une église."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async () => {
    if (!churchFormValid(suggest, SUGGEST_REQUIRED_KEYS)) {
      toast.error(t("churches.required_fields", "Merci de compléter tous les champs obligatoires (le site web est facultatif)."));
      return;
    }
    if (!suggest.pastor_user_id) {
      toast.error(t("churches.pick_leader", "Sélectionne le responsable (un prédicateur possédant un compte Yalola)."));
      return;
    }
    try {
      const { data } = await api.post("/churches/suggest", suggest);
      toast.success(data.message);
      setDialogOpen(false);
      setSuggest(emptyChurch());
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  const toggleFollow = async (c, e) => {
    e?.stopPropagation?.();
    if (!user) { requireLogin("follow"); return; }
    try {
      const { data } = await api.post(`/churches/${c.id}/follow`);
      setChurches((list) => list.map((x) => (x.id === c.id ? { ...x, is_following: data.following, followers_count: data.followers_count } : x)));
      toast.success(data.following ? `Tu suis ${c.name} 🔔` : t("church.unfollowed", "Tu ne suis plus cette église"));
    } catch { toast.error("Erreur"); }
  };

  return (
    <AppShell hideRightRail>
      <div className="space-y-4">
        <div className="yalola-card p-6" data-testid="churches-hero">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Church size={20} style={{ color: "var(--yalola-secondary)" }} />
                <h1 className="text-3xl lg:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>{t("churches.find_title", "Trouve une église")}</h1>
              </div>
              <p className="mt-1 text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("churches.find_subtitle", "Recherche par ville, pays, obédience ou responsable...")}</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (o) prefillResponsible(); }}>
              <DialogTrigger asChild>
                {canSuggest ? (
                  <Button className="pill-primary rounded-full" data-testid="suggest-church-btn">
                    <Plus size={16} className="mr-1" /> {t("churches.suggest", "Suggérer une église")}
                  </Button>
                ) : <span />}
              </DialogTrigger>
              <DialogContent variant="sheet">
                <DialogHeader><DialogTitle>{t("churches.suggest", "Suggérer une église")}</DialogTitle></DialogHeader>
                <DialogBody>
                  <ChurchForm value={suggest} onChange={setSuggest} testPrefix="suggest" requiredKeys={SUGGEST_REQUIRED_KEYS} preacherPicker checkDuplicate />
                </DialogBody>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel", "Annuler")}</Button>
                  <Button className="pill-primary" onClick={submit} data-testid="suggest-submit">{t("churches.submit_review", "Envoyer pour validation")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid md:grid-cols-4 gap-3 mt-4">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" size={16} />
              <Input
                placeholder={t("churches.search_placeholder", "Nom d'église, ville ou responsable...")}
                className="pl-9"
                list="church-suggest"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                data-testid="church-search"
              />
              <datalist id="church-suggest">
                {suggestOptions.map((o) => <option key={o} value={o} />)}
              </datalist>
            </div>
            <select
              value={country}
              onChange={(e) => { const v = e.target.value; setCountry(v); setGeo(null); setCity(""); setOfficialOnly(false); load({ country: v, city: "", officialOnly: false, lat: null, lon: null }); }}
              className="h-10 rounded-md border px-3 text-sm bg-transparent"
              style={{ borderColor: "var(--yalola-border)" }}
              data-testid="church-country-filter"
            >
              <option value="">{t("churches.all_countries", "Tous les pays")}</option>
              {facets.countries.map((c) => <option key={c} value={c}>{countryLabel(c, lang)}</option>)}
            </select>
            <select
              value={denomination}
              onChange={(e) => { const v = e.target.value; setDenomination(v); setGeo(null); setCity(""); setOfficialOnly(false); load({ denomination: v, city: "", officialOnly: false, lat: null, lon: null }); }}
              className="h-10 rounded-md border px-3 text-sm bg-transparent"
              style={{ borderColor: "var(--yalola-border)" }}
              data-testid="church-denom-filter"
            >
              <option value="">{t("churches.all_denominations", "Toutes les obédiences")}</option>
              {facets.denominations.map((d) => <option key={d} value={d}>{denominationLabel(t, d)}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button variant="outline" className="rounded-full" onClick={nearMe} disabled={locating} data-testid="church-near-me-btn">
              {locating ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <LocateFixed size={15} className="mr-1.5" />} {t("churches.near_me", "Autour de moi")}
            </Button>
            <Button
              variant={officialOnly ? "default" : "outline"}
              className={officialOnly ? "rounded-full pill-primary" : "rounded-full"}
              aria-pressed={officialOnly}
              onClick={() => { const v = !officialOnly; setOfficialOnly(v); setGeo(null); load({ officialOnly: v, lat: null, lon: null }); }}
              data-testid="church-official-filter-btn"
            >
              <ShieldCheck size={15} className="mr-1.5" /> {t("churches.official_only", "Officielles")}
            </Button>
            <Button
              variant={myLangOnly ? "default" : "outline"}
              className={myLangOnly ? "rounded-full pill-primary" : "rounded-full"}
              aria-pressed={myLangOnly}
              onClick={() => { const v = !myLangOnly; setMyLangOnly(v); load({ myLangOnly: v }); }}
              data-testid="church-my-lang-btn"
              title={t("discover.my_lang_hint", "Afficher surtout le contenu dans ma langue")}
              style={{ display: (mlDiscoveryOn && showLangUi) ? undefined : "none" }}
            >
              <Globe size={15} className="mr-1.5" /> {t("discover.my_lang", "Ma langue")} · {myLang.toUpperCase()}
            </Button>
            {(search || country || city || denomination || officialOnly || geo) && (
              <Button variant="ghost" className="rounded-full text-sm" onClick={resetFilters} data-testid="church-reset-btn">{t("churches.reset", "Réinitialiser")}</Button>
            )}
            {geo && (
              <button className="chip" data-testid="church-geo-active" onClick={() => { setGeo(null); load({ lat: null, lon: null }); }} title={t("churches.remove_filter", "Retirer ce filtre")}>
                <LocateFixed size={12} /> {geo.radiusKm} km <X size={12} className="ml-0.5 opacity-70" />
              </button>
            )}
            {city && (
              <button className="chip" data-testid="church-active-city" onClick={() => { setCity(""); load({ city: "" }); }} title={t("churches.remove_filter", "Retirer ce filtre")}>
                <MapPin size={12} /> {city} <X size={12} className="ml-0.5 opacity-70" />
              </button>
            )}
            <Button className="pill-primary rounded-full sm:ml-auto" onClick={() => load()} data-testid="church-search-btn">{t("common.search", "Rechercher")}</Button>
          </div>
          {geo && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2.5" data-testid="church-radius-selector">
              <span className="text-xs font-semibold" style={{ color: "var(--yalola-text-muted)" }}>{t("churches.radius", "Rayon")} :</span>
              {[5, 10, 25, 50].map((km) => {
                const active = Number(geo.radiusKm) === km;
                return (
                  <button
                    key={km}
                    onClick={() => setRadius(km)}
                    className="text-xs font-semibold rounded-full px-3 py-1 border transition-colors"
                    style={active
                      ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "var(--yalola-primary)" }
                      : { background: "transparent", color: "var(--yalola-primary)", borderColor: "var(--yalola-primary)" }}
                    data-testid={`church-radius-${km}`}
                  >{km} km</button>
                );
              })}
            </div>
          )}
        </div>

        {config?.settings?.nearby_map_enabled && geo && churchMapPoints.length > 0 && (
          <div className="yalola-card p-2" data-testid="churches-map-card">
            <NearbyMiniMap
              center={{ lat: geo.lat, lon: geo.lon }}
              points={churchMapPoints}
              height={240}
              onPointClick={(p) => nav(p.type === "live" ? `/live?watch=${p.id}` : `/churches/${p.id}`)}
            />
          </div>
        )}

        {noFilters && spotlight.length > 0 && (
          <div className="yalola-card p-5" data-testid="churches-spotlight">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} style={{ color: "#E0A63C" }} />
              <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>{t("churches.spotlight_title", "Églises à la une")}</h2>
              <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("churches.spotlight_hint", "· les plus actives")}</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
              {spotlight.map((c) => (
                <div
                  key={c.id}
                  onClick={() => nav(`/churches/${c.id}`)}
                  className="snap-start shrink-0 w-60 rounded-2xl border overflow-hidden cursor-pointer yalola-card-hover"
                  style={{ borderColor: "var(--yalola-border)" }}
                  data-testid={`spotlight-church-${c.id}`}
                >
                  <div className="relative h-24">
                    {c.cover_url
                      ? <img src={imageVariant(c.cover_url, 640)} className="absolute inset-0 w-full h-full object-cover" alt="" />
                      : <ChurchCoverFallback church={c} className="absolute inset-0" />}
                    {c.is_live_now
                      ? <span className="absolute top-2 left-2 chip text-white" style={{ background: "#DC2626", borderColor: "#DC2626" }} data-testid={`spotlight-live-${c.id}`}><Radio size={11} /> {t("churches.live_now", "En direct")}</span>
                      : c.featured
                        ? <span className="absolute top-2 left-2 chip" style={{ background: "#E0A63C", color: "white", borderColor: "#E0A63C" }}><Sparkles size={11} /> {t("churches.featured", "À la une")}</span>
                        : null}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-2">
                      {c.logo_url
                        ? <img src={imageVariant(c.logo_url, 32)} className="h-8 w-8 rounded-lg object-cover border shrink-0" style={{ borderColor: "var(--yalola-border)" }} alt="" />
                        : <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--yalola-secondary)" }}><Church size={15} className="text-white" /></div>}
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{c.name}</div>
                        <div className="text-xs truncate flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><MapPin size={10} /> {c.city}</div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
                      {c.upcoming_events > 0 && <span className="chip"><CalendarDays size={10} /> {c.upcoming_events} {t("churches.events_short", "évén.")}</span>}
                      {c.service_count > 0 && <span className="chip"><Clock size={10} /> {c.service_count} {t("churches.services_short", "culte(s)")}</span>}
                      <span className="chip"><Users size={10} /> {compactNumber(c.followers_count)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && <CardGridSkeleton count={6} cols="md:grid-cols-2" />}
        {!loading && churches.length === 0 && (
          <div className="yalola-card p-8 text-center space-y-4" data-testid="churches-empty">
            <div>
              <div className="mx-auto mb-3 h-12 w-12 rounded-2xl grid place-items-center" style={{ background: "var(--yalola-secondary)" }}>
                <Church size={22} className="text-white" />
              </div>
              <p className="font-semibold text-lg" style={{ fontFamily: "Outfit" }}>{t("churches.none", "Aucune église trouvée.")}</p>
              <p className="text-sm mt-1" style={{ color: "var(--yalola-text-muted)" }}>
                {noFilters
                  ? t("churches.none_hint_empty", "Sois le premier à ajouter une église de ta région. Elle sera visible après validation.")
                  : t("churches.none_hint", "Aucun résultat pour ces filtres. Élargis ta recherche, ou suggère l'église manquante — nous l'ajouterons après validation.")}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {!noFilters && (
                <Button variant="outline" className="rounded-full" onClick={resetFilters} data-testid="churches-empty-reset">
                  {t("churches.reset_filters", "Réinitialiser les filtres")}
                </Button>
              )}
              {canSuggest && (
                <Button className="pill-primary rounded-full" onClick={() => { prefillResponsible(); setDialogOpen(true); }} data-testid="churches-empty-suggest">
                  <Plus size={16} className="mr-1" /> {t("churches.suggest", "Suggérer une église")}
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {churches.map(c => (
            <div
              key={c.id}
              className="yalola-card yalola-card-hover p-5 cursor-pointer overflow-hidden"
              onClick={() => nav(`/churches/${c.id}`)}
              data-testid={`church-card-${c.id}`}
            >
              <ChurchCover church={c} coverVideoOn={coverVideoOn} t={t} />
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3 min-w-0">
                  {c.logo_url ? (
                    <img src={imageVariant(c.logo_url, 44)} alt="" className="h-11 w-11 rounded-xl object-cover shrink-0 border" style={{ borderColor: "var(--yalola-border)" }} data-testid={`church-card-logo-${c.id}`} />
                  ) : (
                    <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-secondary)" }}><Church size={20} className="text-white" /></div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-semibold text-lg truncate" style={{ fontFamily: "Outfit" }}>{c.name}</h3>
                    <div className="text-sm flex items-center gap-1 flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
                      <MapPin size={14} /> {c.city}, {countryLabel(c.country, lang)}
                      {c.distance_km != null && <span className="font-semibold" style={{ color: "var(--yalola-primary)" }}>· à {String(c.distance_km).replace(".", ",")} km</span>}
                    </div>
                  </div>
                </div>
                {(c.featured || c.verified || c.managed_by_preacher) && (
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {c.featured && (
                      <span className="chip shrink-0" style={{ background: "#E0A63C22", color: "var(--yalola-accent-text)", borderColor: "#E0A63C" }} data-testid={`church-featured-${c.id}`}>
                        <Sparkles size={12} /> {t("churches.featured", "À la une")}
                      </span>
                    )}
                    {c.managed_by_preacher && (
                      <span className="chip shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)", borderColor: "var(--yalola-primary)" }} data-testid={`church-official-${c.id}`}>
                        <ShieldCheck size={12} /> {t("churches.official", "Officielle")}
                      </span>
                    )}
                    {c.verified && (
                      <span className="chip shrink-0" style={{ background: "var(--yalola-accent)22", color: "var(--yalola-accent-text)", borderColor: "var(--yalola-accent)" }}>
                        <ShieldCheck size={12} /> {t("churches.verified", "Vérifiée")}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {(c.pastor_name || c.pastor_title) && (
                <div className="mt-2 text-sm flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>
                  <User size={13} /> {[c.pastor_title, c.pastor_name].filter(Boolean).join(" ")}
                </div>
              )}
              {c.denomination && <div className="mt-2 text-xs chip">{denominationLabel(t, c.denomination)}</div>}
              {c.description && <p className="mt-3 text-sm line-clamp-2" style={{ color: "var(--yalola-text-muted)" }}>{c.description}</p>}
              <div className="mt-4 flex items-center justify-between gap-2">
                <span className="text-xs flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>
                  <Users size={13} /> {compactNumber(c.followers_count || 0)} {t("churches.followers", "abonné(s)")}
                </span>
                <Button
                  size="sm"
                  variant={c.is_following ? "outline" : "default"}
                  className={c.is_following ? "rounded-full" : "rounded-full pill-primary"}
                  onClick={(e) => toggleFollow(c, e)}
                  data-testid={`church-follow-${c.id}`}
                >
                  {c.is_following ? <><Check size={14} className="mr-1" /> {t("churches.following", "Suivie")}</> : <><Bell size={14} className="mr-1" /> {t("churches.follow", "Suivre")}</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
};

export default Churches;
